// src/pages/FriendshipModulesPage.jsx - the MindEase Vibe modules grid + viewer
// (file name is clone-era; the page is MindEase's)
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Filter, Award, BookOpen, Target, Brain, Lock, CheckCircle,
  ArrowRight, Clock, Star, Zap, Crown, Heart, Headphones,
  Search, Shield, Users, Sparkles, Flame, Telescope, Settings,
  Globe, Rocket, ArrowLeft,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useGamificationStore } from '../stores/gamificationStore';
// Lazy load the large LearningModuleViewer component (344KB!)
const LearningModuleViewer = React.lazy(() => import('../components/modules/LearningModuleViewer'));
import EnhancedTeenModuleCard from '../components/modules/EnhancedTeenModuleCard';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';

// Module content and the card metadata derived from it
import { mindeaseModules as friendshipModules, moduleCategories } from '../data/index.js';
import { mindeaseModulesData as friendshipModulesData } from '../data/mindeaseModulesMetadata.js';

const FriendshipModulesPage = () => {
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSeries, setFilterSeries] = useState('all');
  const [initialView, setInitialView] = useState(null);


  const {
    assessmentResults,
    getModuleProgress = () => ({ progressPercentage: 0 }),
    isModuleCompleted = () => false,
    completeModule,
    updateModuleProgress,
    setCurrentView,
  } = useApp();

  // ZUSTAND: Selective subscriptions - only re-renders when these specific values change
  const completedModules = useGamificationStore((state) => state.completedModules);
  const currentLevel = useGamificationStore((state) => state.currentLevel);
  const currentStreak = useGamificationStore((state) => state.currentStreak);
  const gamificationCompleteModule = useGamificationStore((state) => state.completeModule);
  const gamificationAddXP = useGamificationStore((state) => state.addXP);
  const gamificationUpdateStreak = useGamificationStore((state) => state.updateStreak);

  // Check for returnToModule in localStorage on component mount
  useEffect(() => {
    const returnToModuleId = localStorage.getItem('returnToModule');
    const returnToExercises = localStorage.getItem('returnToExercises');

    if (returnToModuleId) {
      const moduleId = parseInt(returnToModuleId, 10);
      setSelectedModuleId(moduleId);

      // Check if we should go directly to exercises
      if (returnToExercises === 'true') {
        setInitialView('exercises');
        localStorage.removeItem('returnToExercises');
        // Clear the initial view after a moment to avoid persistent state
        setTimeout(() => setInitialView(null), 100);
      }

      // Clear the localStorage item after use
      localStorage.removeItem('returnToModule');
    }
  }, []);

  // Filter options come from the modules themselves, in module order.
  const levels = ['all', ...new Set(friendshipModulesData.map((m) => m.level))];
  const categories = ['all', ...new Set(friendshipModulesData.map((m) => m.category))];
  const series = ['all', ...new Set(friendshipModulesData.map((m) => m.series))];

  // Filter modules
  let filteredModules = friendshipModulesData;

  if (filterLevel !== 'all') {
    filteredModules = filteredModules.filter(module => module.level === filterLevel);
  }

  if (filterCategory !== 'all') {
    filteredModules = filteredModules.filter(module => module.category === filterCategory);
  }

  if (filterSeries !== 'all') {
    filteredModules = filteredModules.filter(module => module.series === filterSeries);
  }

  // Get recommendations
  const getRecommendations = () => {
    const available = friendshipModulesData.filter(module =>
      !completedModules.includes(module.id),
    );

    return available.slice(0, 3);
  };

  const recommendedModules = getRecommendations();

  // Check if module is completed - using Zustand store
  const checkModuleCompleted = (moduleId) => {
    return completedModules.includes(moduleId);
  };

  // Flexible unlock logic for friendship modules
  const canAccessModule = (module) => {
    // Modules 1-6 are immediately accessible
    if (module.id >= 1 && module.id <= 6) {
      return true;
    }

    // Module 7 (Social Confidence Code) requires completing 2 modules from 1-6
    // TEMP: Completely bypassed for development work
    if (module.id === 7) {
      const foundationCount = completedModules.filter(id => id >= 1 && id <= 6).length;
      // Debug logging disabled to prevent console spam
      // console.log('🔍 Module 7 access check:', { completedModules: completedModules.length, gamificationModules: gamification?.completedModules?.length || 0 });
      return true; // TEMP: Always allow access for development
    }

    // Module 8 (Friendship Mastery) requires completing 4 modules from 1-7
    // TEMP: Completely bypassed for development work
    if (module.id === 8) {
      const foundationCount = completedModules.filter(id => id >= 1 && id <= 7).length;
      // Debug logging disabled to prevent console spam
      // console.log('🔍 Module 8 access check:', { completedModules: completedModules.length, gamificationModules: gamification?.completedModules?.length || 0 });
      return true; // TEMP: Always allow access for development
    }

    return true;
  };

  const getModuleStatus = (module) => {
    if (isModuleCompleted(module.id)) return 'completed';

    if (!canAccessModule(module)) {
      return 'locked';
    }

    const moduleProgress = getModuleProgress(module.id);
    if (moduleProgress && moduleProgress.progressPercentage > 0) {
      return 'inProgress';
    }

    return 'available';
  };

  const getUnlockHint = (module) => {
    if (canAccessModule(module)) return null;

    // Module 7 requires 2 completed modules from 1-6
    if (module.id === 7) {
      const foundationCount = completedModules.filter(id => id >= 1 && id <= 6).length;
      const needed = 2 - foundationCount;
      return `Complete ${needed} more module${needed > 1 ? 's' : ''} to unlock`;
    }

    // Module 8 requires 4 completed modules from 1-7
    if (module.id === 8) {
      const foundationCount = completedModules.filter(id => id >= 1 && id <= 7).length;
      const needed = 4 - foundationCount;
      return `Complete ${needed} more module${needed > 1 ? 's' : ''} to unlock`;
    }

    return null; // Modules 1-6 should not need unlock hints
  };

  const completionPercentage = Math.round((completedModules.length / friendshipModulesData.length) * 100);

  // Auto-select next available module for Continue Learning when no specific module is set
  // FIXED: Only run on mount to prevent infinite loops from completedModules changing
  useEffect(() => {
    // Check if this is from Continue Learning (indicated by URL param or localStorage)
    const urlParams = new URLSearchParams(window.location.search);
    const isFromContinueLearning = urlParams.get('continue') === 'true' || localStorage.getItem('continuelearning') === 'true';

    // Only auto-select if this is specifically from Continue Learning
    if (!selectedModuleId && isFromContinueLearning && completedModules.length >= 0) {
      const availableModules = friendshipModulesData.filter(module =>
        canAccessModule(module) &&
        !completedModules.includes(module.id) &&
        friendshipModules.find(fm => fm.id === module.id), // Has actual content
      );

      if (availableModules.length > 0) {
        // Choose the lowest numbered available module (next in sequence)
        const nextModule = availableModules.sort((a, b) => a.id - b.id)[0];
        setSelectedModuleId(nextModule.id);

        // Clear the continue learning flag
        localStorage.removeItem('continuelearning');
        // Remove URL param to clean up
        if (urlParams.get('continue')) {
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - uses initial completedModules value

  // Handle module selection
  const handleModuleClick = (moduleId) => {
    const module = friendshipModulesData.find(m => m.id === moduleId);
    if (module) {
      setSelectedModuleId(moduleId);
    }
  };

  const handleBackToModules = () => {
    setSelectedModuleId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleModuleComplete = async (moduleData = {}) => {
    const moduleId = moduleData.moduleId || selectedModuleId;

    if (!moduleId) {
      console.warn('⚠️ No module ID for completion');
      return;
    }

    try {
      // Complete the module (this handles progress updates internally)
      console.log('🔍 Completing module:', moduleId, 'Current completed modules:', completedModules);
      await completeModule(moduleId, {
        progressPercentage: 100,
        completed: true,
        completedAt: new Date().toISOString(),
        ...moduleData,
      });
      console.log('🔍 Module completion called');

      // Handle gamification updates safely
      try {
        if (gamificationCompleteModule) {
          gamificationCompleteModule(moduleId);
        }
        if (gamificationAddXP) {
          const module = friendshipModulesData.find(m => m.id === moduleId);
          gamificationAddXP(module?.xpReward || 100, `Completed Module ${moduleId}`);
        }
        if (gamificationUpdateStreak) {
          gamificationUpdateStreak();
        }
      } catch (gamificationError) {
        console.warn('⚠️ Gamification update failed:', gamificationError);
        // Continue with module completion even if gamification fails
      }

      // Find the next available module with content
      // Include the just-completed module in the completed count for prerequisite checking
      const updatedCompletedModules = [...completedModules, moduleId];


      const availableModules = friendshipModulesData.filter(module => {
        // Skip the module we just completed
        if (updatedCompletedModules.includes(module.id)) return false;

        // Check if module has actual content
        if (!friendshipModules.find(fm => fm.id === module.id)) return false;

        // Check access with updated completed modules list
        if (module.id >= 1 && module.id <= 6) {
          return true; // Modules 1-6 are immediately accessible
        }

        if (module.id === 7) {
          const foundationCount = updatedCompletedModules.filter(id => id >= 1 && id <= 6).length;
          return foundationCount >= 1; // TEMP: Reduced for development work
        }

        if (module.id === 8) {
          const foundationCount = updatedCompletedModules.filter(id => id >= 1 && id <= 7).length;
          return foundationCount >= 4;
        }

        return true;
      });

      console.log('🔍 Available modules after completion:', {
        availableModules: availableModules.map(m => ({ id: m.id, title: m.title })),
        updatedCompletedModules,
      });

      setTimeout(() => {
        if (availableModules.length > 0) {
          // Find the next sequential module (the one immediately after the completed module)
          const currentModuleIndex = friendshipModulesData.findIndex(m => m.id === moduleId);
          const nextSequentialModule = friendshipModulesData[currentModuleIndex + 1];

          // Check if the next sequential module is in our available modules list
          if (nextSequentialModule && availableModules.some(m => m.id === nextSequentialModule.id)) {
            setSelectedModuleId(nextSequentialModule.id);
          } else {
            // If next sequential isn't available, find the next available module by ID
            const nextAvailable = availableModules.sort((a, b) => a.id - b.id)[0];
            setSelectedModuleId(nextAvailable.id);
          }
        } else {
          // No more available modules - go back to modules list
          setSelectedModuleId(null);
        }
      }, 1500);

    } catch (error) {
      console.error('❌ Error completing module:', error);
      setSelectedModuleId(null);
    }
  };


  // If a module is selected, show the module viewer
  if (selectedModuleId) {
    const selectedModule = friendshipModulesData.find(m => m.id === selectedModuleId);
    const friendshipModule = friendshipModules.find(m => m.id === selectedModuleId);

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Navigation Header */}
        <div className="fixed top-28 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={handleBackToModules}
                className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Modules
              </button>

              <div className="flex items-center gap-4">
                <div className="text-sm text-slate-400">Module {selectedModule?.id}</div>
                <div className="text-sm font-medium text-white">{selectedModule?.title}</div>
              </div>

              <button
                onClick={() => setCurrentView('dashboard')}
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Module Content */}
        <div className="pt-20">
          {friendshipModule ? (
            <React.Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
                  <div className="text-white text-lg font-semibold">Loading Module...</div>
                  <div className="text-purple-300 text-sm mt-2">Preparing your learning experience</div>
                </div>
              </div>
            }>
              <LearningModuleViewer
                module={friendshipModule}
                onBack={handleBackToModules}
                onComplete={handleModuleComplete}
                initialView={initialView}
              />
            </React.Suspense>
          ) : (
            <div className="container mx-auto px-4 py-12 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Module content coming soon!</h2>
              <p className="text-slate-300 mb-6">This module is being prepared for you.</p>
              <Button onClick={handleBackToModules}>
                Back to Modules
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-32 pb-20">
      <div className="container mx-auto px-4 py-8 pb-16">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 mb-4"
          >
            <Heart className="w-6 h-6 text-vibe-ocean-light" />
            <span className="text-vibe-ocean-light font-medium">Dive In</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            Turn the volume down on anxiety
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed"
          >
            Eight modules on what anxiety is doing, why it is loudest at your age, and
            <span className="text-purple-300 font-semibold"> what to do in the ten minutes after it fires.</span>
          </motion.p>
        </div>

        {/* How to Dive In Guide */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <Card className="bg-gradient-to-r from-teal-500/10 to-purple-500/10 border border-teal-500/30 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-teal-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                  How to Dive In
                </h3>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-green-400 font-bold text-xs">1</span>
                    </div>
                    <div>
                      <div className="text-green-400 font-semibold mb-1">Start Where You Want</div>
                      <div className="text-slate-300">Every module is open. Start with Module 1 if anxiety is new territory, or go straight to the one that matches what is going on for you.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-purple-400 font-bold text-xs">2</span>
                    </div>
                    <div>
                      <div className="text-purple-400 font-semibold mb-1">Learn & Practise</div>
                      <div className="text-slate-300">Each module is a few short parts, and each part stands on its own. The tools it points to are built for that module.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-orange-400 font-bold text-xs">3</span>
                    </div>
                    <div>
                      <div className="text-orange-400 font-semibold mb-1">Track Progress</div>
                      <div className="text-slate-300">Your dashboard keeps what you have finished and what you saved. Nothing here is homework.</div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle className="w-4 h-4 text-teal-400" />
                    <span>If you took the Vibe Check, your dashboard suggests where to start.</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Progress Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border-purple-500/20">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Your Progress</h3>
                  <p className="text-purple-200">
                    {completedModules.length} of {friendshipModulesData.length} modules completed
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white">Where you are</h3>
                  <p className="text-green-200">
                    {completionPercentage < 25 ? 'Getting started' :
                     completionPercentage < 50 ? 'Building the kit' :
                     completionPercentage < 75 ? 'Using the kit' :
                     completionPercentage < 100 ? 'Nearly through' : 'All eight done'}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  {completionPercentage}%
                </div>
                <div className="text-sm text-slate-400">Complete</div>
              </div>
            </div>

            <div className="mt-6 h-3 bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500"
                initial={{ width: 0 }}
                animate={{ width: `${completionPercentage}%` }}
                transition={{ duration: 1, delay: 0.5 }}
              />
            </div>
          </Card>
        </motion.div>

        {/* Recommendations */}
        {recommendedModules.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-8"
          >
            <Card className="p-6">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-400" />
                Recommended for You
              </h3>
              <p className="text-slate-300 mb-6">
                Good places to go next:
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {recommendedModules.map((module) => (
                  <EnhancedTeenModuleCard
                    key={module.id}
                    module={module}
                    isCompleted={checkModuleCompleted(module.id)}
                    canAccess={canAccessModule(module)}
                    progress={getModuleProgress(module.id).progressPercentage || 0}
                    onClick={() => handleModuleClick(module.id)}
                    isRecommended={true}
                    unlockHint={getUnlockHint(module)}
                    userLevel={currentLevel}
                    currentStreak={currentStreak}
                  />
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Filter Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8"
        >
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <Filter className="w-5 h-5 text-purple-400" />

              <div className="flex gap-2 flex-wrap">
                <span className="text-slate-400 text-sm font-medium">Level:</span>
                {levels.map((level) => (
                  <button
                    key={level}
                    onClick={() => setFilterLevel(level)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                      filterLevel === level
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 flex-wrap">
                <span className="text-slate-400 text-sm font-medium">Category:</span>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setFilterCategory(category)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                      filterCategory === category
                        ? 'bg-green-600 text-white shadow-lg'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* All Modules Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-400" />
            All eight modules
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredModules.map((module, index) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.8 }}
                className="h-full"
              >
                <EnhancedTeenModuleCard
                  module={module}
                  isCompleted={checkModuleCompleted(module.id)}
                  canAccess={canAccessModule(module)}
                  progress={getModuleProgress(module.id).progressPercentage || 0}
                  onClick={() => handleModuleClick(module.id)}
                  isRecommended={recommendedModules.some(rec => rec.id === module.id)}
                  unlockHint={getUnlockHint(module)}
                  userLevel={currentLevel}
                  currentStreak={currentStreak}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {filteredModules.length === 0 && (
          <Card className="p-8 text-center">
            <div className="text-4xl mb-4">🤔</div>
            <h3 className="text-xl font-semibold text-white mb-2">No modules match your filters</h3>
            <p className="text-slate-400 mb-6">Try adjusting your filters to discover more content.</p>
            <Button onClick={() => {
              setFilterLevel('all');
              setFilterCategory('all');
              setFilterSeries('all');
            }}>
              Show All Modules
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FriendshipModulesPage;